import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { CustomFieldGroupType, CustomFieldType, LinkedCustomFieldGroupObjectType, LinkedCustomFieldObjectType } from "./CustomFieldTypes";
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import CustomFieldEditor from './CustomFieldEditor';
import { useTranslation } from 'react-i18next';
import CustomFieldGroupEditor from './CustomFieldGroupEditor';
import { useAuthContext } from 'services/authentication/AuthenticationContext';

interface CustomFieldBuilderProps {
    groups: CustomFieldGroupType[]; 
    setGroups: React.Dispatch<React.SetStateAction<CustomFieldGroupType[]>>; 
    fields: CustomFieldType[]; 
    setFields: React.Dispatch<React.SetStateAction<CustomFieldType[]>>; 
    supportsGroups: boolean; 
    extendedOptionsFeature: 'custom_fields_extended' | 'forms_extended';
    selectedLinkedObjectType?: LinkedCustomFieldObjectType; 
    selectedLinkedObjectValue?: string; 
    showFieldPreview?: boolean;
    canAddField?: boolean;
    fieldLimitMessage?: string;
};

const CustomFieldBuilder: React.FC<CustomFieldBuilderProps> = ({
    groups,
    setGroups,
    fields,
    setFields,
    supportsGroups,
    extendedOptionsFeature,
    selectedLinkedObjectType,
    selectedLinkedObjectValue,
    showFieldPreview,
    canAddField = true,
    fieldLimitMessage
}) => {
    const { t } = useTranslation();
    const { activeFeatures } = useAuthContext();
    const allowExtendedOptions = activeFeatures?.includes(extendedOptionsFeature) ?? false;

    // Determines wheter the first empty fields may be created
    const canCreateFields = selectedLinkedObjectType && (selectedLinkedObjectValue || selectedLinkedObjectType === 'form' || selectedLinkedObjectType === 'form_template');

    // Update the ordering when reordering the fields
    const reorderFields = (fieldsToReorder: CustomFieldType[]) => {
        return fieldsToReorder.map((custom_field, index) => ({
            ...custom_field,
            ordering: index + 1,
        }));
    };

    // Add a new group
    const handleAddGroup = () => {
        if (supportsGroups && canCreateFields) {

            // Generate a group uuid
            const groupUuid = uuidv4();

            // Create a new group
            const newGroup: CustomFieldGroupType = {
                uuid: groupUuid,
                linked_object_type: selectedLinkedObjectType as LinkedCustomFieldGroupObjectType,
                linked_object_value: selectedLinkedObjectValue || null,
                title: '',
                description: '',
                ordering: groups.filter(group => !group.deleted).length + 1,
                pdf_width: 'full_width',
            };

            // Add the first empty field set
            const newField: CustomFieldType = {
                uuid: uuidv4(),
                linked_object_type: selectedLinkedObjectType,
                linked_object_value: selectedLinkedObjectValue,
                group_id: groupUuid,
                parent_field_id: null,
                label: '',
                description: '',
                placeholder: '',
                field_type: 'input',
                required: false,
                hide_externally: false,
                ordering: 1,
                options: [],
            };

            // Update the group and field states
            setGroups(current => [...current, newGroup]);
            setFields(current => [ ...current, newField ]);
        }
    };
    
    // Add a new field, unless the custom field limit blocks new fields
    const handleAddField = (groupId?: number | string | null, parentFieldId?: number | string | null) => {
        if (canCreateFields && canAddField) {
            const resolvedGroupId = groupId || null;
            const resolvedParentFieldId = parentFieldId || null;

            // Update the fields state
            setFields(current => {
                const newField: CustomFieldType = {
                    uuid: uuidv4(),
                    linked_object_type: selectedLinkedObjectType,
                    linked_object_value: selectedLinkedObjectValue || null,
                    group_id: resolvedGroupId,
                    parent_field_id: resolvedParentFieldId,
                    label: '',
                    description: '',
                    placeholder: '',
                    field_type: 'input',
                    required: false,
                    hide_externally: false,
                    ordering: 0,
                    options: [],
                };

                // Get the relevant fields to order
                const orderFields = [
                    ...current.filter(custom_field =>
                        custom_field.group_id === resolvedGroupId &&
                        custom_field.parent_field_id === resolvedParentFieldId &&
                        !custom_field.deleted
                    ),
                    newField,
                ];

                // Reorder the fields
                const reorderedFields = reorderFields(orderFields);

                // Return the reordered fields
                return [
                    ...current.filter(custom_field =>
                        custom_field.group_id !== resolvedGroupId ||
                        custom_field.parent_field_id !== resolvedParentFieldId ||
                        custom_field.deleted
                    ),
                    ...reorderedFields,
                ];
            });
        }
    };

    // Filter ungrouped fields and sort them
    const ungroupedFields = fields
        .filter(custom_field =>
            !custom_field.group_id &&
            !custom_field.parent_field_id &&
            !custom_field.deleted
        )
        .sort((firstField, secondField) => firstField.ordering - secondField.ordering);

    return (
        <div className='custom-field-builder'>
            {/* Ungrouped fields */}
            <DragDropContext
                onDragEnd={(result: DropResult) => {
                    if (result.destination) {
                        const reorderedFields = [...ungroupedFields];

                        const [removedField] = reorderedFields.splice(result.source.index, 1);

                        reorderedFields.splice(result.destination.index, 0, removedField);

                        const updatedFields = reorderFields(reorderedFields);

                        setFields(current => {
                            const otherFields = current.filter(custom_field =>
                                custom_field.group_id ||
                                custom_field.parent_field_id ||
                                custom_field.deleted
                            );

                            return [
                                ...otherFields,
                                ...updatedFields,
                            ];
                        });
                    }
                }}
            >
                <StrictModeDroppable droppableId='ungrouped-fields'>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className='ungrouped-fields'
                        >
                            {ungroupedFields.map((field, index) => (
                                <Draggable
                                    key={field.id || field.uuid}
                                    draggableId={`field-${field.id || field.uuid}`}
                                    index={index}
                                >
                                    {(provided) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                        >
                                            <CustomFieldEditor
                                                field={field}
                                                fields={fields}
                                                setFields={setFields}
                                                selectedLinkedObjectType={selectedLinkedObjectType}
                                                showFieldPreview={showFieldPreview}
                                                allowExtendedOptions={allowExtendedOptions}
                                                extendedOptionsFeature={extendedOptionsFeature}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
            </DragDropContext>

            <div onClick={(e) => {e.preventDefault(); handleAddField(); }} 
                    className={`add-new-button ${canAddField ? '' : 'disabled'}`}>
                {t('custom_field.general.add_field_label')}
            </div>

            {/* Show the limit notice when no more fields may be added */}
            {!canAddField && fieldLimitMessage && (
                <p className='custom-field-limit-notice'>
                    {fieldLimitMessage}
                </p>
            )}

            {/* Grouped fields if available */}
            {supportsGroups && (
                <>
                    <DragDropContext
                        onDragEnd={(result: DropResult) => {
                            if (result.destination) {
                                const reorderedGroups = [...groups];
                                const [removedGroup] = reorderedGroups.splice(result.source.index, 1);
                                reorderedGroups.splice(result.destination.index, 0, removedGroup);

                                const updatedGroups = reorderedGroups.map((group, index) => ({
                                    ...group,
                                    ordering: index + 1,
                                }));

                                setGroups(updatedGroups);
                            }
                        }}
                    >
                        <StrictModeDroppable droppableId='groups'>
                            {(provided) => (
                                <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    className='groups'
                                >
                                    {groups
                                        .filter(group => !group.deleted)
                                        .sort((firstGroup, secondGroup) => firstGroup.ordering - secondGroup.ordering)
                                        .map((group, index) => (
                                            <Draggable
                                                key={group.id || group.uuid}
                                                draggableId={`group-${group.id || group.uuid}`}
                                                index={index}
                                            >
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                    >
                                                        <CustomFieldGroupEditor
                                                            dragHandleProps={provided.dragHandleProps}
                                                            group={group}
                                                            fields={fields}
                                                            showFieldPreview={showFieldPreview}
                                                            selectedLinkedObjectType={selectedLinkedObjectType}
                                                            allowExtendedOptions={allowExtendedOptions}
                                                            extendedOptionsFeature={extendedOptionsFeature}
                                                            setFields={setFields}
                                                            setGroups={setGroups}
                                                            onAddField={handleAddField}
                                                        />
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))
                                    }
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    <div onClick={(e) => {e.preventDefault(); handleAddGroup(); }} 
                        className="add-new-button">
                        {t('custom_field.general.add_section_label')}
                    </div>
                </>
            )}
        </div>
    )
};

export default CustomFieldBuilder;